import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SanityTextsProvider } from './contexts/SanityTextsContext';
import StaggeredMenu from './components/StaggeredMenu';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import NewsPage from './pages/NewsPage';
import NewsArticlePage from './pages/NewsArticlePage';
import PortfolioPage from './pages/PortfolioPage';
import PortfolioItemPage from './pages/PortfolioItemPage';
import ContactPage from './pages/ContactPage';
import TeamPage from './pages/TeamPage';
import { getWebsiteTexts } from './sanity/client';
import './i18n/i18n';
import './App.css';

function App() {
  const { t, i18n } = useTranslation();
  const [menuTexts, setMenuTexts] = useState([]);
  
  // Fetch menu texts from Sanity
  useEffect(() => {
    const fetchMenuTexts = async () => {
      try {
        const texts = await getWebsiteTexts('menu');
        setMenuTexts(texts);
      } catch (error) {
        console.error('Error fetching menu texts:', error);
      }
    };
    fetchMenuTexts();
  }, []);

  // Helper to get localized text from Sanity
  const getMenuText = (key, fallback) => {
    const textItem = menuTexts.find(item => item.key === key);
    if (!textItem) return fallback;
    return i18n.language === 'it' ? textItem.textIt : textItem.textEn;
  };
  
  const menuItems = [
    { label: getMenuText('home', t('nav.home')), ariaLabel: 'Go to home page', link: '/' },
    { label: getMenuText('about', t('nav.about')), ariaLabel: 'Learn about us', link: '/about' },
    { label: getMenuText('team', t('nav.team')), ariaLabel: 'Meet our team', link: '/team' },
    { label: getMenuText('portfolio', t('nav.portfolio')), ariaLabel: 'View our portfolio', link: '/portfolio' },
    { label: getMenuText('news', t('nav.news')), ariaLabel: 'Read our latest news', link: '/news' },
    { label: getMenuText('contact', t('nav.contact')), ariaLabel: 'Get in touch', link: '/contact' }
  ];

  const socialItems = [
    { label: 'Instagram', link: 'https://instagram.com/magicomms' },
    { label: 'LinkedIn', link: 'https://www.linkedin.com/company/magicomms' }
  ];
  // Fix for mobile browser viewport height (URL bar and bottom navigation)
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  return (
    <HelmetProvider>
      <SanityTextsProvider>
        <Router>
          <div className="App">
            {/* Staggered Menu Overlay */}
            <StaggeredMenu
              position="right"
              items={menuItems}
              socialItems={socialItems}
              displaySocials={true}
              displayItemNumbering={true}
              menuButtonColor="#ffffff"
              openMenuButtonColor="#1E1E1C"
              changeMenuColorOnOpen={true}
              colors={['#1e1e1c', '#3a3a3a', '#5a5a5a']}
              logoUrl={process.env.PUBLIC_URL + "/svg/M_white.svg"}
              logoUrlDark={process.env.PUBLIC_URL + "/svg/M_black.svg"}
              accentColor="#1E1E1C"
              isFixed={true}
              onMenuOpen={() => console.log('Menu opened')}
              onMenuClose={() => console.log('Menu closed')}
            />

            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/portfolio/:slug" element={<PortfolioItemPage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<NewsArticlePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/team" element={<TeamPage />} />
            </Routes>

            <Footer />
            <CookieConsent />
          </div>
        </Router>
      </SanityTextsProvider>
    </HelmetProvider>
  );
}

export default App;
